import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/ppcp";

/** What the reader has already put in the trolley.
 *
 *  One store per program. Item ids are minted by the server and carry their own section key, so a
 *  week's fresh list and the monthly staples never share state. Writes are optimistic, exactly like
 *  meal ticks: the box fills immediately and the server's list replaces it when the save lands. */
const ShoppingContext = createContext(null);

export const ShoppingProvider = ({ slug, enabled = true, children }) => {
  const [checked, setChecked] = useState(null);

  useEffect(() => {
    if (!enabled) return;
    let live = true;
    setChecked(null);
    api
      .get(`/programs/${slug}/grocery/items`)
      .then((res) => live && setChecked(res.data.checked ?? []))
      .catch(() => live && setChecked([]));
    return () => {
      live = false;
    };
  }, [slug, enabled]);

  const toggle = useCallback(
    async (itemId, next) => {
      const previous = checked ?? [];
      setChecked(next ? [...new Set([...previous, itemId])] : previous.filter((i) => i !== itemId));
      try {
        const { data } = await api.put(`/programs/${slug}/grocery/items/${itemId}`, {
          checked: next,
        });
        setChecked(data.checked ?? []);
      } catch {
        setChecked(previous);
        toast.error("We couldn't save that just now. Please try again.");
      }
    },
    [slug, checked]
  );

  /** Puts a cleared list back, so Clear list is never a one-way door. */
  const restore = useCallback(
    async (ids) => {
      if (!ids?.length) return;
      setChecked((current) => [...new Set([...(current ?? []), ...ids])]);
      try {
        const { data } = await api.post(`/programs/${slug}/grocery/items/restore`, { ids });
        setChecked(data.checked ?? []);
      } catch {
        toast.error("We couldn't restore that list just now. Please try again.");
      }
    },
    [slug]
  );

  /** Empties one list. Returns what was cleared so the caller can offer an undo. */
  const clear = useCallback(
    async (sectionKey) => {
      const previous = checked ?? [];
      const cleared = previous.filter((i) => i.startsWith(`${sectionKey}/`));
      setChecked(previous.filter((i) => !i.startsWith(`${sectionKey}/`)));
      try {
        const { data } = await api.post(`/programs/${slug}/grocery/items/clear`, {
          section_key: sectionKey,
        });
        setChecked(data.checked ?? []);
        return cleared;
      } catch {
        setChecked(previous);
        toast.error("We couldn't clear that list just now. Please try again.");
        return [];
      }
    },
    [slug, checked]
  );

  const value = useMemo(
    () => ({ checked: checked ?? [], ready: checked !== null, toggle, clear, restore }),
    [checked, toggle, clear, restore]
  );
  return <ShoppingContext.Provider value={value}>{children}</ShoppingContext.Provider>;
};

export const useShopping = () => useContext(ShoppingContext);
